<script setup lang="ts">
import ProgramEnrollmentDialog from '@/components/eos/program-enrollment/ProgramEnrollmentDialog.vue'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'

import { useProgramEnrollments } from '@/composables/eos/program-enrollments'
import { formatEnrollmentDate, formatEnrollmentEndDate } from '@/helpers/eos/table-formatters'
import type { IProgramEnrollmentFilters } from '@/types/eos/ProgramEnrollment'
import { computed, reactive, ref } from 'vue'

const { programEnrollmentsQuery } = useProgramEnrollments()

const filters = reactive<IProgramEnrollmentFilters>({})

const { data, error, isError, isFetching, isPending, refetch } = programEnrollmentsQuery(filters)

const dialogVisible = ref(false)
const editSiteId = ref<string | null>(null)

/** Enrollments of the site selected for edit. We pass these into the dialog/form. */
const editEnrollments = computed(() => {
  if (!editSiteId.value) return null
  return siteCards.value.find((s) => s.siteId === editSiteId.value)?.enrollments ?? null
})

function openEdit(siteId: string) {
  editSiteId.value = siteId
  dialogVisible.value = true
}

function openNew() {
  editSiteId.value = null
  dialogVisible.value = true
}

const siteCards = computed(() => {
  if (!data.value?.length) return []

  return data.value.map(({ site, program_enrollments }) => {
    // The site's live ISO (from the enrollments endpoint); a dash when unset.
    const iso = site.iso ?? '—'
    const typeMap = new Map<string, { name: string; timeline: string }[]>()

    for (const enrollment of program_enrollments) {
      const { type } = enrollment.program
      if (!typeMap.has(type)) typeMap.set(type, [])
      const timeline = `${formatEnrollmentDate(enrollment.start_enrollment_period_utc, site.timezone ?? undefined)} - ${
        enrollment.end_enrollment_period_utc
          ? formatEnrollmentEndDate(enrollment.end_enrollment_period_utc, site.timezone ?? undefined)
          : 'No end date'
      }`
      typeMap.get(type)!.push({ name: enrollment.program.name, timeline })
    }

    return {
      siteId: site.id,
      siteName: site.name,
      iso,
      enrollments: program_enrollments,
      typeGroups: Array.from(typeMap.entries()).map(([type, programs]) => ({ type, programs })),
    }
  })
})
</script>

<template>
  <ViewLayout>
    <template #controls>
      <h1 class="text-2xl font-bold">
        <el-button type="primary" class="actions-button" @click="openNew()">Enroll</el-button>
      </h1>
    </template>

    <!-- Dialog -->
    <ProgramEnrollmentDialog
      v-model="dialogVisible"
      :existing-enrollments="editEnrollments"
      @created="dialogVisible = false"
    />

    <!-- Card grid -->
    <div class="w-full flex flex-col items-center justify-center text-gray-400 self-center">
      <!-- Loading -->
      <div v-if="isPending" class="h-xl flex items-center justify-center">Loading...</div>
      <!-- Error -->
      <rl-error-result v-else-if="isError" :error="error">
        <el-button @click="refetch()">
          <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
        </el-button>
      </rl-error-result>
      <div v-else-if="siteCards.length" class="grid grid-cols-2 gap-4 w-full">
        <!-- Site card -->
        <el-card v-for="site in siteCards" :key="site.siteId" v-loading="isFetching" class="group border-0">
          <template #header>
            <div class="flex flex-col gap-3">
              <div class="flex items-center justify-between">
                <span class="font-medium">{{ site.siteName }}</span>
                <Icon
                  icon="material-symbols:edit-outline"
                  :height="16"
                  class="cursor-pointer text-text-tertiary invisible group-hover:visible"
                  @click="openEdit(site.siteId)"
                />
              </div>
              <span>{{ site.iso }}</span>
            </div>
          </template>

          <!-- Program type group -->
          <div v-for="group in site.typeGroups" :key="group.type" class="mb-3 last:mb-0">
            <p class="text-sm text-text-tertiary mb-1">
              {{ group.type }}
            </p>
            <ul class="space-y-2">
              <!-- Program -->
              <li
                v-for="program in group.programs"
                :key="program.name"
                class="text-sm flex items-center justify-between"
              >
                <span class="max-w-7/10">{{ program.name }}</span>
                <span class="text-grayscale-300">{{ program.timeline }}</span>
              </li>
            </ul>
          </div>
        </el-card>
      </div>
      <span v-else>No enrollments found</span>
    </div>
  </ViewLayout>
</template>

<style lang="scss" scoped>
:deep() {
  .el-card {
    &__header {
      border-bottom: none;
      padding-bottom: 4px;
    }
  }
}
</style>
